import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { useProcessBulkWatchers } from "@ryde/redux";
import {
  errors,
  isProcessCanceledByUser,
  processAtom,
  ProcessesApi,
  processSelector,
  ProcessStatusEnum,
  resolveProcessProgressTitle,
  simpleResultErrorEmptyResponse,
  TRANSLATION_KEY,
  useProcesses,
  warnAndToastSimpleResult,
  type RydeProcessTitle,
  type RydeUiUnit,
} from "@ryde/shared";

import { resolveProcessProgressColor } from "./process-drawer.helpers";

export const useProcessDrawer = ({
  processId,
  entity,
  processTitle,
  currentEntityPage,
  avoidResetSelectedIds,
  companyId,
  onCloseDrawer,
}: {
  processId: number;
  entity: RydeUiUnit;
  processTitle: RydeProcessTitle;
  currentEntityPage?: RydeUiUnit;
  avoidResetSelectedIds?: boolean;
  companyId?: number;
  onCloseDrawer: () => void;
}) => {
  const { t } = useTranslation();
  const { startProcessPolling, stopProcessPolling } = useProcesses();
  const setProcesses = useSetRecoilState(processAtom);
  const processes = useRecoilValue(processSelector);
  const [isAborting, setIsAborting] = useState(false);
  const currentProcess = useMemo(() => {
    return processes.get(processId);
  }, [processes, processId]);
  const isPending = currentProcess?.status === ProcessStatusEnum.Pending;
  const progressValue = currentProcess?.progress ?? undefined;
  const progressTitleDefault = t("process.progressTitle", {
    entity: t(`${entity}.single`),
    processTitle: t(
      `process.${currentProcess?.title ?? "defaultProgressProcessTitle"}`,
    ),
  });
  const progressTitle = resolveProcessProgressTitle({
    t,
    process: currentProcess,
    defaultTitle: progressTitleDefault,
    isAborting,
  });
  const progressColor = resolveProcessProgressColor(currentProcess);
  const cancelLabel = useMemo(() => {
    if (isPending && !isProcessCanceledByUser(currentProcess)) {
      return t("process.abortOperationAndClose");
    }

    return t("general.CLOSE");
  }, [currentProcess, isPending, t]);

  useProcessBulkWatchers({
    entity,
    currentEntityPage,
    avoidResetSelectedIds,
  });

  const applyCanceledProcessLocally = useEvent((): void => {
    setProcesses(prev => {
      const existing = prev.get(processId);
      if (existing?.status !== ProcessStatusEnum.Pending) {
        return prev;
      }

      const copy = new Map(prev);
      copy.set(processId, {
        ...existing,
        status: ProcessStatusEnum.Failed,
        errors: [
          {
            [TRANSLATION_KEY]: errors.processes.processCanceledByUser,
          },
        ],
      });
      return copy;
    });
  });

  const handleProcessAbort = useEvent(async (): Promise<void> => {
    if (!processId || !isPending || isAborting) {
      return;
    }

    setIsAborting(true);
    try {
      const apiResult = await ProcessesApi.cancel(processId, companyId);

      if (!apiResult) {
        warnAndToastSimpleResult(simpleResultErrorEmptyResponse(t), t);
        return;
      }

      if (!apiResult.success) {
        warnAndToastSimpleResult(apiResult, t);
        return;
      }

      applyCanceledProcessLocally();
      const refreshed = await ProcessesApi.getOne(processId, companyId);
      if (refreshed) {
        setProcesses(prev => {
          const copy = new Map(prev);
          copy.set(processId, refreshed);
          return copy;
        });
      }
    } finally {
      setIsAborting(false);
    }
  });

  const onCloseHandler = useCallback(async () => {
    if (isPending) {
      await handleProcessAbort();
    }

    onCloseDrawer();
  }, [handleProcessAbort, isPending, onCloseDrawer]);

  useEffect(() => {
    if (!processId) {
      return;
    }

    startProcessPolling({
      id: processId,
      processTitle,
      entity,
      companyId,
    });

    return () => {
      stopProcessPolling({
        id: processId,
        entity,
        processTitle,
      });
    };
  }, [
    entity,
    processId,
    processTitle,
    companyId,
    startProcessPolling,
    stopProcessPolling,
  ]);

  useEffect(() => {
    if (isAborting && isProcessCanceledByUser(currentProcess)) {
      setIsAborting(false);
    }
  }, [currentProcess, isAborting]);

  return {
    progressValue,
    progressTitle,
    progressColor,
    currentProcess,
    isPending,
    isAborting,
    handleProcessAbort,
    onCloseHandler,
    cancelLabel,
  };
};
